import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import { CheckCircle2, Save } from "lucide-react";

const STATUS_LIST = [
  { key: "ordered", label: "Ordered", desc: "Sent when admin marks the order as in production." },
  { key: "ready_for_pickup", label: "Ready for Pickup", desc: "Sent when the screens are built and waiting at the shop." },
  { key: "ready_for_delivery", label: "Ready for Delivery", desc: "Sent when the order is queued for our driver." },
  { key: "completed", label: "Completed", desc: "Sent after the order is delivered/picked up. Asks for a Google review." },
  { key: "paid", label: "Paid (re-send)", desc: "Default off. Use to re-send the original confirmation if needed." },
];

const VARIABLES = [
  { tag: "{first_name}", note: "Customer's first name" },
  { tag: "{customer_name}", note: "Full name" },
  { tag: "{order_number}", note: "e.g. SZ-A1B2" },
  { tag: "{total}", note: "Order total formatted as $X.XX" },
  { tag: "{product_name}", note: "Product name" },
  { tag: "{quantity}", note: "Item quantity" },
  { tag: "{manage_link}", note: "One-click link to /manage/{id}" },
  { tag: "{measurement_link}", note: "Link to measurement form" },
  { tag: "{brand}", note: "Brand name (ScreenzZ)" },
];

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeStatus, setActiveStatus] = useState("ordered");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/notification-templates");
      setTemplates(r.data || []);
    } catch {
      toast.error("Couldn't load templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Sync draft when active status changes or templates reload.
  useEffect(() => {
    const found = templates.find((t) => t.status === activeStatus);
    setDraft(found ? { ...found } : null);
  }, [activeStatus, templates]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await api.put(`/admin/notification-templates/${draft.status}`, {
        sms_enabled: draft.sms_enabled,
        sms_body: draft.sms_body,
        email_enabled: draft.email_enabled,
        email_subject: draft.email_subject,
        email_body: draft.email_body,
      });
      setTemplates((rows) => rows.map((t) => t.status === draft.status ? r.data : t));
      toast.success("Template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const insertVar = (tag) => {
    if (!draft) return;
    // Append the variable to whichever field was last focused. We track this
    // via a simple ref-less approach: read from document.activeElement.
    const el = document.activeElement;
    if (!el || !el.dataset || !el.dataset.tplField) {
      toast.info("Click into a field first, then click a variable.");
      return;
    }
    const field = el.dataset.tplField;
    const cur = draft[field] || "";
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + tag + cur.slice(end);
    setDraft({ ...draft, [field]: next });
    // Re-focus + position cursor after insertion.
    setTimeout(() => {
      try {
        el.focus();
        const pos = start + tag.length;
        el.setSelectionRange(pos, pos);
      } catch { /* ignore */ }
    }, 0);
  };

  const dirty = draft && templates.find((t) => t.status === draft.status) &&
    JSON.stringify({
      sms_enabled: draft.sms_enabled, sms_body: draft.sms_body,
      email_enabled: draft.email_enabled, email_subject: draft.email_subject, email_body: draft.email_body,
    }) !==
    JSON.stringify({
      sms_enabled: templates.find((t) => t.status === draft.status).sms_enabled,
      sms_body: templates.find((t) => t.status === draft.status).sms_body,
      email_enabled: templates.find((t) => t.status === draft.status).email_enabled,
      email_subject: templates.find((t) => t.status === draft.status).email_subject,
      email_body: templates.find((t) => t.status === draft.status).email_body,
    });

  return (
    <AdminLayout>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[var(--brand-text)]">Notification templates</h1>
        <p className="text-sm muted-text mt-1">
          Edit the SMS + email customers receive when an order changes status. The rendered message is shown in a confirmation modal before sending — you can still tweak it per-order from the order detail.
        </p>
      </div>

      {loading || !draft ? (
        <p className="muted-text">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_240px] gap-5">
          {/* Status sidebar */}
          <div className="space-y-1.5" data-testid="tpl-status-list">
            {STATUS_LIST.map((s) => {
              const tpl = templates.find((t) => t.status === s.key);
              const enabled = (tpl?.sms_enabled || tpl?.email_enabled);
              const isActive = s.key === activeStatus;
              return (
                <button
                  key={s.key}
                  data-testid={`tpl-status-${s.key}`}
                  onClick={() => setActiveStatus(s.key)}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm transition border ${
                    isActive
                      ? "brand-bg text-white border-transparent"
                      : "bg-white border-[var(--brand-border)] hover:border-[var(--brand-green)]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{s.label}</span>
                    {enabled && (
                      <CheckCircle2 className="w-3.5 h-3.5 ml-auto" style={isActive ? { color: "#fff" } : { color: "var(--brand-green)" }} />
                    )}
                  </div>
                  <p className={`text-[11px] mt-0.5 ${isActive ? "text-white/80" : "muted-text"}`}>
                    {s.desc}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Editor */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-[var(--brand-text)]">SMS</h2>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid="tpl-sms-enabled"
                    type="checkbox"
                    checked={!!draft.sms_enabled}
                    onChange={(e) => setDraft({ ...draft, sms_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>
              <textarea
                data-testid="tpl-sms-body"
                data-tpl-field="sms_body"
                value={draft.sms_body || ""}
                onChange={(e) => setDraft({ ...draft, sms_body: e.target.value })}
                rows={4}
                maxLength={1600}
                className="w-full rounded-xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm font-mono"
                placeholder="Hi {first_name}, your order {order_number}…"
              />
              <p className="text-[11px] muted-text mt-1">
                {(draft.sms_body || "").length} chars · 1 SMS ≈ 160 chars · long messages are split into multiple sends.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-[var(--brand-text)]">Email</h2>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid="tpl-email-enabled"
                    type="checkbox"
                    checked={!!draft.email_enabled}
                    onChange={(e) => setDraft({ ...draft, email_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>
              <label className="text-xs font-semibold muted-text">Subject</label>
              <input
                data-testid="tpl-email-subject"
                data-tpl-field="email_subject"
                value={draft.email_subject || ""}
                onChange={(e) => setDraft({ ...draft, email_subject: e.target.value })}
                maxLength={200}
                className="w-full rounded-xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm mt-1 mb-3"
                placeholder="{brand} — order {order_number} update"
              />
              <label className="text-xs font-semibold muted-text">Body (plain text — line breaks preserved)</label>
              <textarea
                data-testid="tpl-email-body"
                data-tpl-field="email_body"
                value={draft.email_body || ""}
                onChange={(e) => setDraft({ ...draft, email_body: e.target.value })}
                rows={9}
                maxLength={8000}
                className="w-full rounded-xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm font-mono mt-1"
                placeholder="Hi {first_name},\n\nGood news — your order {order_number} is…"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="tpl-revert-btn"
                onClick={() => {
                  const found = templates.find((t) => t.status === activeStatus);
                  if (found) setDraft({ ...found });
                }}
                disabled={!dirty || saving}
                className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-[var(--brand-border)] disabled:opacity-50"
              >
                Revert
              </button>
              <button
                data-testid="tpl-save-btn"
                onClick={save}
                disabled={!dirty || saving}
                className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold brand-bg text-white disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save template"}
              </button>
            </div>
          </div>

          {/* Variables panel */}
          <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-4 h-fit md:sticky md:top-4">
            <h3 className="font-bold text-[var(--brand-text)] mb-1">Variables</h3>
            <p className="text-[11px] muted-text mb-3">
              Click into a field, then click a variable to insert it.
            </p>
            <div className="space-y-1.5">
              {VARIABLES.map((v) => (
                <button
                  key={v.tag}
                  data-testid={`tpl-var-${v.tag.replace(/[{}]/g, "")}`}
                  // preventDefault on mousedown so the click doesn't steal
                  // focus from the textarea / input we're trying to insert
                  // into. Without this, document.activeElement in insertVar
                  // is the button itself and we silently bail out.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertVar(v.tag)}
                  className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[var(--brand-green-light)] border border-transparent hover:border-[var(--brand-green)] transition"
                >
                  <p className="font-mono text-[12px] brand-text">{v.tag}</p>
                  <p className="text-[10px] muted-text">{v.note}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
